/**
 * Waveform utilities for audio visualization.
 *
 * Pure functions for generating and extracting waveform data used by the
 * AudioPlayer component. Separated from the component for reusability and
 * testability.
 *
 * @see AudioPlayer — apps/web/src/ui/components/audio-player.tsx
 *
 * TODO: Add unit tests when web testing infrastructure is in place.
 * Key cases to cover:
 *   - formatAudioTime: NaN, Infinity, 0, fractional seconds, >60min
 *   - generatePlaceholderHeights: deterministic output for same seed, bar count, value ranges
 *   - extractPeaks: normalization, MIN_BAR_HEIGHT floor, single-sample chunks
 */

export const BAR_COUNT = 40;
export const MIN_BAR_HEIGHT = 0.08;

/**
 * Format seconds into a human-readable `m:ss` or `h:mm:ss` string for audio
 * playback display. Returns `0:00` for non-finite inputs.
 */
export function formatAudioTime(seconds: number): string {
  if (!Number.isFinite(seconds) || Number.isNaN(seconds)) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Generate deterministic pseudo-random bar heights from a seed string.
 * Used as a placeholder waveform while the real audio data is loading.
 * Heights are normalized to [0.25, 1.0] with a bell-curve envelope.
 */
export function generatePlaceholderHeights(seed: string, count: number): number[] {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  }
  return Array.from({ length: count }, (_, i) => {
    hash = (Math.imul(hash, 1664525) + 1013904223 + i) | 0;
    const normalized = Math.abs(hash % 1000) / 1000;
    const shaped = 0.25 + normalized * 0.75;
    const center = count / 2;
    const distFromCenter = Math.abs(i - center) / center;
    const envelope = 1.0 - distFromCenter * 0.3;
    return Math.min(1.0, shaped * envelope);
  });
}

/**
 * Extract peak amplitudes from a decoded AudioBuffer. Divides the audio
 * data into `count` equal chunks and takes the max absolute sample from
 * each. Peaks are normalized against the global max so the tallest bar
 * is always 1.0, with a floor of MIN_BAR_HEIGHT.
 */
export function extractPeaks(buffer: AudioBuffer, count: number): number[] {
  const data = buffer.getChannelData(0);
  const chunkSize = Math.floor(data.length / count);
  const peaks = Array.from<number>({ length: count });

  for (let i = 0; i < count; i++) {
    let peak = 0;
    const start = i * chunkSize;
    const end = Math.min(start + chunkSize, data.length);
    for (let j = start; j < end; j++) {
      const abs = Math.abs(data[j]);
      if (abs > peak) peak = abs;
    }
    peaks[i] = peak;
  }

  const max = Math.max(...peaks) || 1;
  return peaks.map((p) => Math.max(MIN_BAR_HEIGHT, p / max));
}

let sharedAudioCtx: AudioContext | null = null;

/** Lazily create and reuse a single AudioContext for decoding. */
export function getAudioContext(): AudioContext {
  if (!sharedAudioCtx) sharedAudioCtx = new AudioContext();
  return sharedAudioCtx;
}
